import {
  ArrowRightIcon,
  EnvelopeIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import type {
  ContactDepartment,
  ContactPerson,
  ContactPhone,
  SalesDesk,
} from "@type/contact";
import Link from "next/link";
import useTranslation from "next-translate/useTranslation";

import { ContactIcon, MarketFlagIcon } from "./contact-icon";
import { telHref } from "./contact-utils";
import { CopyButton } from "./copy-button";
import { SectionHeading } from "./section-heading";

interface DepartmentsProps {
  departments: ContactDepartment[];
  salesDesk: SalesDesk | null;
}

export const Departments = ({ departments, salesDesk }: DepartmentsProps) => {
  const { t } = useTranslation("contact");

  return (
    <section
      aria-labelledby="departments-title"
      className="scroll-mt-28 bg-[#f6f4ef] py-16 md:py-24"
      id="departments"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          description={t("departments.description")}
          eyebrow={t("departments.eyebrow")}
          id="departments-title"
          title={t("departments.title")}
        />

        {salesDesk && <SalesDeskCard salesDesk={salesDesk} />}

        <ul className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {departments.map((department) => (
            <li
              className="scroll-mt-28"
              id={`dept-${department.id}`}
              key={department.id}
            >
              <DepartmentCard department={department} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

const SalesDeskCard = ({ salesDesk }: { salesDesk: SalesDesk }) => {
  const { t } = useTranslation("contact");

  return (
    <div
      className="scroll-mt-28 overflow-hidden rounded-2xl bg-[#051124] text-white shadow-xl"
      id="sales"
    >
      <div className="grid gap-6 p-6 md:p-8 lg:grid-cols-[minmax(0,17rem)_1fr] lg:gap-8">
        <div>
          <div className="flex items-center gap-3">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-white/10 text-[#c1a166]">
              <ContactIcon className="size-5" name="users" />
            </span>
            <div>
              <h3 className="font-semibold text-lg">{salesDesk.title}</h3>
              <p className="text-white/60 text-xs">{salesDesk.subtitle}</p>
            </div>
          </div>
          <p className="mt-4 text-sm text-white/75 leading-relaxed">
            {salesDesk.description}
          </p>
        </div>

        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
          {salesDesk.markets.map((market) => {
            const isHighlighted = market.id === salesDesk.highlightedMarketId;
            return (
              <li
                className={`flex items-start justify-between gap-4 rounded-xl border p-4 sm:block ${
                  isHighlighted
                    ? "border-[#c1a166] bg-[#c1a166]/10"
                    : "border-white/10 bg-white/[0.03]"
                }`}
                key={market.id}
              >
                <div className="flex flex-wrap items-center gap-2 pt-0.5 sm:pt-0">
                  <MarketFlagIcon flag={market.flag} />
                  <span className="font-semibold text-sm">{market.label}</span>
                  {isHighlighted && (
                    <span className="rounded bg-[#c1a166] px-1.5 py-0.5 font-bold text-[#051124] text-[10px] uppercase tracking-wide">
                      {t("departments.yourMarket")}
                    </span>
                  )}
                </div>
                <ul className="space-y-1 text-right sm:mt-3 sm:text-left">
                  {market.phones.map((phone, index) => (
                    <li key={phone}>
                      <a
                        className={`block whitespace-nowrap transition hover:text-[#c1a166] ${
                          index === 0
                            ? "font-semibold text-sm"
                            : "text-white/70 text-xs"
                        }`}
                        href={telHref(phone)}
                      >
                        {phone}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

const DepartmentCard = ({ department }: { department: ContactDepartment }) => {
  const [mainPhone, ...restPhones] = department.phones;
  const headedPhones = restPhones.filter((phone) => phone.heading);
  const otherPhones = restPhones.filter((phone) => !phone.heading);

  return (
    <article className="flex h-full flex-col rounded-xl border border-gray-200/80 bg-white p-5 shadow-sm md:p-6">
      <header className="flex items-start gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[#c1a166]/15 text-[#8a6d2f]">
          <ContactIcon className="size-5" name={department.icon} />
        </span>
        <div className="min-w-0">
          <h3 className="font-semibold text-[#051124] leading-snug">
            {department.title}
          </h3>
          {department.subtitle && (
            <p className="text-gray-500 text-xs">{department.subtitle}</p>
          )}
        </div>
      </header>

      {mainPhone && (
        <div className="mt-5">
          <MainPhone phone={mainPhone} />
        </div>
      )}

      {headedPhones.map((phone) => (
        <div className="mt-4 border-gray-100 border-t pt-4" key={phone.number}>
          <h4 className="font-semibold text-gray-500 text-sm uppercase tracking-wide">
            {phone.heading}
          </h4>
          <div className="mt-1.5">
            <MainPhone phone={phone} />
          </div>
        </div>
      ))}

      {(otherPhones.length > 0 || department.emails.length > 0) && (
        <ul className="mt-3 divide-y divide-gray-100 border-gray-100 border-t">
          {otherPhones.map((phone) => (
            <ContactRow
              key={phone.number}
              note={phone.note}
              type="phone"
              value={phone.number}
            />
          ))}
          {department.emails.map((email) => (
            <ContactRow key={email} type="email" value={email} />
          ))}
        </ul>
      )}

      {department.people && department.people.length > 0 && (
        <ul className="mt-4 space-y-3">
          {department.people.map((person) => (
            <PersonItem key={person.name} person={person} />
          ))}
        </ul>
      )}

      {department.link && (
        <Link
          className="mt-auto inline-flex items-center gap-1.5 pt-4 font-semibold text-[#8a6d2f] text-sm hover:text-[#051124]"
          href={department.link.href}
        >
          {department.link.label}
          <ArrowRightIcon aria-hidden="true" className="size-4" />
        </Link>
      )}
    </article>
  );
};

const MainPhone = ({ phone }: { phone: ContactPhone }) => (
  <div className="flex items-center justify-between gap-2">
    <a
      className="inline-flex items-center gap-2.5 font-bold text-[#051124] text-xl tracking-tight transition hover:text-[#8a6d2f] md:text-2xl"
      href={telHref(phone.number)}
    >
      <PhoneIcon aria-hidden="true" className="size-5 text-[#c1a166]" />
      {phone.number}
    </a>
    <CopyButton value={phone.number} />
  </div>
);

interface ContactRowProps {
  note?: string;
  type: "email" | "phone";
  value: string;
}

const ContactRow = ({ note, type, value }: ContactRowProps) => {
  const isEmail = type === "email";
  const Icon = isEmail ? EnvelopeIcon : PhoneIcon;

  return (
    <li className="flex items-center justify-between gap-2 py-1.5">
      <a
        className={`flex min-w-0 items-center gap-2.5 text-sm transition hover:text-[#8a6d2f] ${
          isEmail ? "font-medium text-[#8a6d2f]" : "text-gray-700"
        }`}
        href={isEmail ? `mailto:${value}` : telHref(value)}
      >
        <Icon aria-hidden="true" className="size-4 shrink-0 text-gray-400" />
        <span className={isEmail ? "break-all" : "whitespace-nowrap"}>
          {value}
        </span>
        {note && (
          <span className="truncate rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-500">
            {note}
          </span>
        )}
      </a>
      <CopyButton value={value} />
    </li>
  );
};

const PersonItem = ({ person }: { person: ContactPerson }) => (
  <li className="rounded-lg bg-[#f6f4ef] p-4">
    <p className="font-semibold text-[#051124] text-sm">{person.name}</p>
    {person.role && <p className="text-gray-500 text-xs">{person.role}</p>}
    <ul className="mt-2">
      {person.phones.map((phone) => (
        <ContactRow
          key={phone.number}
          note={phone.note}
          type="phone"
          value={phone.number}
        />
      ))}
      {person.email && <ContactRow type="email" value={person.email} />}
    </ul>
  </li>
);
